import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  Badge,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  Snackbar,
  Paper,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import {
  Phone,
  Email,
  Warning,
  Schedule,
  TrendingUp,
  TrendingDown,
  DirectionsCar,
  Person,
  Assignment,
  Refresh,
  FilterList,
  Download,
  Add,
  Visibility,
  PriorityHigh,
  AccessTime,
  CalendarToday,
  Business,
  Groups,
  CheckCircle,
  PendingActions,
  NotificationImportant,
  Send,
  ContactPhone,
  Close,
} from '@mui/icons-material';
import { followUpService, type FollowUpItem, type FollowUpNote } from '@/services/followUpService';
import toast from 'react-hot-toast';

interface QuickStats {
  totalPending: number;
  highPriority: number;
  overdue: number;
  completedToday: number;
  driversPending: number;
  hostsPending: number;
  resolved: number;
}

const FollowUpDashboardPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FollowUpItem | null>(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' as 'success' | 'error' });
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<FollowUpNote[]>([]);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'call' | 'email' | 'meeting' | 'system'>('call');
  const [selectedItemForNote, setSelectedItemForNote] = useState<FollowUpItem | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [followUpItems, setFollowUpItems] = useState<FollowUpItem[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats>({
    totalPending: 0,
    highPriority: 0,
    overdue: 0,
    completedToday: 0,
    driversPending: 0,
    hostsPending: 0,
    resolved: 0
  });
  const [error, setError] = useState<string | null>(null);

  // Create new follow-up form data
  const [newFollowUp, setNewFollowUp] = useState({
    type: 'driver' as 'driver' | 'host',
    title: '',
    description: '',
    urgency: 'medium' as 'low' | 'medium' | 'high',
    userEmail: '',
    userPhone: '',
    userName: '',
    assignedTo: ''
  });

  // Load follow-ups from API
  const loadFollowUps = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await followUpService.getFollowUps();

      // Update state even if API failed but we have fallback data
      if (result.data) {
        setFollowUpItems(result.data.followUps);

        // Update quick stats
        setQuickStats({
          totalPending: result.data.summary.byStatus?.open || 0,
          highPriority: result.data.summary.byUrgency.high,
          overdue: 0, // TODO: Calculate from due dates
          completedToday: 0, // TODO: Get from backend
          driversPending: result.data.followUps.filter(f =>
            (f.type === 'verification_pending' || f.type === 'incomplete_onboarding' || f.type === 'driver') &&
            f.status === 'open'
          ).length,
          hostsPending: result.data.followUps.filter(f =>
            (f.type === 'pending_host_approval' || f.type === 'host') &&
            f.status === 'open'
          ).length,
          resolved: result.data.summary.byStatus?.resolved || 0
        });

        // Show warning if API failed but we have localStorage fallback
        if (!result.success && result.error) {
          setError(`API unavailable: ${result.error}. Showing locally stored follow-ups only.`);
        }
      }
    } catch (err) {
      console.error('Error loading follow-ups:', err);
      setError(err instanceof Error ? err.message : 'Failed to load follow-ups');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadFollowUps();
  }, []);

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getProgressColor = (completionPercentage?: number) => {
    const progress = completionPercentage || 0;
    if (progress >= 80) return 'success';
    if (progress >= 50) return 'warning';
    return 'error';
  };

  const getTabFilteredItems = () => {
    let tabFilteredItems = followUpItems;

    // Apply tab-based filtering
    switch (selectedTab) {
      case 0: // All Follow-ups
        tabFilteredItems = followUpItems.filter(item => item.status !== 'resolved');
        break;
      case 1: // Drivers
        tabFilteredItems = followUpItems.filter(item =>
          (item.type === 'driver' || item.type === 'verification_pending' || item.type === 'incomplete_onboarding') &&
          item.status !== 'resolved'
        );
        break;
      case 2: // Hosts
        tabFilteredItems = followUpItems.filter(item =>
          (item.type === 'host' || item.type === 'pending_host_approval') &&
          item.status !== 'resolved'
        );
        break;
      case 3: // Resolved
        tabFilteredItems = followUpItems.filter(item => item.status === 'resolved');
        break;
      default:
        tabFilteredItems = followUpItems;
    }

    // Apply additional filters
    return tabFilteredItems.filter(item => {
      const matchesUrgency = urgencyFilter === 'all' || item.urgency === urgencyFilter;
      const matchesType = typeFilter === 'all' ||
        (typeFilter === 'driver' && (item.type === 'driver' || item.type === 'verification_pending' || item.type === 'incomplete_onboarding')) ||
        (typeFilter === 'host' && (item.type === 'host' || item.type === 'pending_host_approval'));
      return matchesUrgency && matchesType;
    });
  };

  const filteredItems = getTabFilteredItems();

  const handleContact = (item: FollowUpItem) => {
    setSelectedItem(item);
    setOpenContactDialog(true);
  };

  const handleViewDetails = (item: FollowUpItem) => {
    setSelectedItem(item);
    setViewDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setViewDetailsOpen(false);
    setSelectedItem(null);
  };

  const handleExportReport = () => {
    setSnackbar({ open: true, message: 'Report exported successfully', type: 'success' });
  };

  const handleRefreshData = async () => {
    await loadFollowUps();
    toast.success('Data refreshed successfully');
  };

  const handleCreateFollowUp = async () => {
    if (!newFollowUp.title.trim() || !newFollowUp.userEmail.trim()) {
      toast.error('Please fill in required fields (title and user email)');
      return;
    }

    try {
      setIsLoading(true);
      const result = await followUpService.createFollowUp({
        type: newFollowUp.type === 'driver' ? 'driver' : 'host',
        title: newFollowUp.title,
        description: newFollowUp.description,
        urgency: newFollowUp.urgency,
        userId: '', // Will be generated
        userInfo: {
          email: newFollowUp.userEmail,
          phone: newFollowUp.userPhone,
          name: newFollowUp.userName || newFollowUp.userEmail
        },
        assignedTo: newFollowUp.assignedTo || undefined
      });

      if (result.success) {
        toast.success('Follow-up created successfully');
        setCreateDialogOpen(false);
        setNewFollowUp({
          type: 'driver',
          title: '',
          description: '',
          urgency: 'medium',
          userEmail: '',
          userPhone: '',
          userName: '',
          assignedTo: ''
        });
        await loadFollowUps();
      } else {
        toast.error(result.error || 'Failed to create follow-up');
      }
    } catch (err) {
      toast.error('Failed to create follow-up');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveFollowUp = async () => {
    if (!selectedItem) return;

    try {
      setIsLoading(true);
      const result = await followUpService.resolveFollowUp(selectedItem.id, followUpNote);

      if (result.success) {
        toast.success('Follow-up resolved successfully');
        setResolveDialogOpen(false);
        setFollowUpNote('');
        await loadFollowUps();
      } else {
        toast.error(result.error || 'Failed to resolve follow-up');
      }
    } catch (err) {
      toast.error('Failed to resolve follow-up');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCall = () => {
    if (selectedItem) {
      setSnackbar({ open: true, message: `Calling ${selectedItem.name}...`, type: 'success' });
      setOpenContactDialog(false);
    }
  };

  const handleSendEmail = () => {
    if (selectedItem) {
      setSnackbar({ open: true, message: `Email sent to ${selectedItem.name}`, type: 'success' });
      setOpenContactDialog(false);
    }
  };

  const handleRecordFollowUp = () => {
    if (!followUpNote.trim()) {
      setSnackbar({ open: true, message: 'Please enter a follow-up note', type: 'error' });
      return;
    }
    
    setSnackbar({ open: true, message: 'Follow-up recorded successfully', type: 'success' });
    setFollowUpNote('');
    setOpenContactDialog(false);
  };

  const handleViewNotes = (item: FollowUpItem) => {
    const notes = followUpService.getNotesFor(item.id);
    setSelectedNotes(notes);
    setSelectedItemForNote(item);
    setNotesDialogOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesDialogOpen(false);
    setSelectedNotes([]);
    setSelectedItemForNote(null);
  };

  const handleAddNote = () => {
    setNewNote('');
    setNoteType('call');
    setAddNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    if (!selectedItemForNote) {
      toast.error('No item selected for note');
      return;
    }

    try {
      const result = await followUpService.addNote(selectedItemForNote.id, {
        content: newNote.trim(),
        author: 'Current User', // TODO: Get from logged-in user
        type: noteType
      });

      if (result.success && result.data) {
        // Add to selected notes list
        const updatedNotes = [...selectedNotes, result.data];
        setSelectedNotes(updatedNotes);

        toast.success(`Note added successfully to ${selectedItemForNote.userInfo.name}'s follow-up`);

        // Close dialog and reset form
        setAddNoteDialogOpen(false);
        setNewNote('');
        setNoteType('call');
      } else {
        toast.error(result.error || 'Failed to add note');
      }
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleCloseAddNote = () => {
    setAddNoteDialogOpen(false);
    setNewNote('');
    setNoteType('call');
  };

  const getNoteIcon = (type: string) => {
    switch (type) {
      case 'call': return <Phone />;
      case 'email': return <Email />;
      case 'meeting': return <Groups />;
      case 'system': return <Assignment />;
      default: return <Assignment />;
    }
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'call': return 'primary';
      case 'email': return 'secondary';
      case 'meeting': return 'success';
      case 'system': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Follow-up Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage driver onboarding and host listing completion
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
            Create New Follow-Up
          </Button>
          <Button variant="outlined" startIcon={<Download />} onClick={handleExportReport}>
            Export Report
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={handleRefreshData} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && !followUpItems.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Pending
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {quickStats.totalPending}
                  </Typography>
                </Box>
                <PendingActions sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    High Priority
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'error.main' }}>
                    {quickStats.highPriority}
                  </Typography>
                </Box>
                <PriorityHigh sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Overdue
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'warning.main' }}>
                    {quickStats.overdue}
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Drivers Pending
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {quickStats.driversPending}
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Hosts Pending
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {quickStats.hostsPending}
                  </Typography>
                </Box>
                <DirectionsCar sx={{ fontSize: 40, color: 'secondary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed Today
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: 'success.main' }}>
                    {quickStats.completedToday}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption" color="success.main">
                      +23%
                    </Typography>
                  </Box>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Urgent Action Required</AlertTitle>
            8 follow-ups are overdue and require immediate attention
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <Alert severity="warning">
            <AlertTitle>High Priority Items</AlertTitle>
            12 high-priority follow-ups need attention within 24 hours
          </Alert>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All Open (${followUpItems.filter(item => item.status !== 'resolved').length})`} />
            <Tab label={`Drivers (${followUpItems.filter(item =>
              (item.type === 'driver' || item.type === 'verification_pending' || item.type === 'incomplete_onboarding') &&
              item.status !== 'resolved'
            ).length})`} />
            <Tab label={`Hosts (${followUpItems.filter(item =>
              (item.type === 'host' || item.type === 'pending_host_approval') &&
              item.status !== 'resolved'
            ).length})`} />
            <Tab label={`Resolved (${followUpItems.filter(item => item.status === 'resolved').length})`} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                  label="Urgency"
                >
                  <MenuItem value="all">All Urgencies</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="driver">Drivers</MenuItem>
                  <MenuItem value="host">Hosts</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Follow-up Items Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Urgency</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedTab === 1 ? 'No drivers found matching the current filters.' :
                           selectedTab === 2 ? 'No hosts found matching the current filters.' :
                           selectedTab === 3 ? 'No resolved items found.' :
                           'No follow-up items found matching the current filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {item.userInfo.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {item.userInfo.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.userInfo.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.type.replace('_', ' ')}
                          size="small"
                          color={item.type.includes('driver') || item.type.includes('verification') ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.metadata?.completionPercentage !== undefined ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={item.metadata.completionPercentage}
                              sx={{ width: 60, height: 6, borderRadius: 3 }}
                              color={getProgressColor(item.metadata.completionPercentage) as any}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {item.metadata.completionPercentage}%
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.urgency}
                          size="small"
                          color={getUrgencyColor(item.urgency) as any}
                          variant={item.urgency === 'high' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                        {item.metadata?.daysPending && (
                          <Typography variant="caption" color="text.secondary">
                            {item.metadata.daysPending} days ago
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {item.assignedTo || 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Contact User">
                            <IconButton size="small" onClick={() => handleContact(item)}>
                              <ContactPhone fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(item)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Notes">
                            <IconButton size="small" onClick={() => handleViewNotes(item)}>
                              <Assignment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {item.status !== 'resolved' && (
                            <Tooltip title="Resolve Follow-Up">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setResolveDialogOpen(true);
                                }}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Contact Dialog */}
      <Dialog open={openContactDialog} onClose={() => setOpenContactDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Contact {selectedItem?.name}
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              {/* Contact Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Contact Information</Typography>
                <Typography variant="body2">📧 {selectedItem.email}</Typography>
                <Typography variant="body2">📞 {selectedItem.phone}</Typography>
                <Typography variant="body2">📍 {selectedItem.location}</Typography>
              </Box>

              {/* Pending Items */}
              <Typography variant="subtitle2" gutterBottom>Pending Items</Typography>
              <List dense sx={{ mb: 3 }}>
                {selectedItem.pendingItems.map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>

              {/* Quick Actions */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth startIcon={<Phone />} onClick={handleCall}>
                    Call
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth startIcon={<Email />} onClick={handleSendEmail}>
                    Send Email
                  </Button>
                </Grid>
              </Grid>

              {/* Follow-up Note */}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Follow-up Note"
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="Record your follow-up action..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContactDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRecordFollowUp}>
            Record Follow-up
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 40, height: 40 }}>
              {selectedItem?.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedItem?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItem?.userCode} • {selectedItem?.type}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box>
              {/* Status and Progress */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  label={selectedItem.urgency}
                  color={getUrgencyColor(selectedItem.urgency) as any}
                />
                <Chip
                  label={selectedItem.type}
                  color={selectedItem.type === 'driver' ? 'primary' : 'secondary'}
                  variant="outlined"
                />
                <Chip
                  label={`${selectedItem.progress}% Complete`}
                  color={getProgressColor(selectedItem.progress) as any}
                  variant="outlined"
                />
              </Box>

              {/* Contact Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Contact Information</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">📧 {selectedItem.email}</Typography>
                      <Typography variant="body2">📞 {selectedItem.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">📍 {selectedItem.location}</Typography>
                      <Typography variant="body2">👤 {selectedItem.assignedTo}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Category and Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Issue Details</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{selectedItem.category}</Typography>
                  <Typography variant="body2">{selectedItem.description}</Typography>
                </Paper>
              </Box>

              {/* Pending Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Pending Items ({selectedItem.pendingItems.length})</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <List dense>
                    {selectedItem.pendingItems.map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemText
                          primary={item}
                          secondary={`Item ${index + 1} of ${selectedItem.pendingItems.length}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>

              {/* Timeline */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Timeline</Typography>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <List dense>
                    {selectedItem.lastFollowUp && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Last Follow-up"
                          secondary={new Date(selectedItem.lastFollowUp).toLocaleString()}
                        />
                      </ListItem>
                    )}
                    {selectedItem.nextFollowUpDue && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Next Follow-up Due"
                          secondary={new Date(selectedItem.nextFollowUpDue).toLocaleString()}
                        />
                      </ListItem>
                    )}
                    {selectedItem.dueDate && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="Due Date"
                          secondary={new Date(selectedItem.dueDate).toLocaleString()}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
          <Button variant="outlined" onClick={() => handleContact(selectedItem!)}>Contact User</Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onClose={handleCloseNotes} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            <Typography variant="h6">Follow-up Notes ({selectedNotes.length})</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNotes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No notes available for this item.
            </Typography>
          ) : (
            <List sx={{ width: '100%' }}>
              {selectedNotes.map((note, index) => (
                <React.Fragment key={note.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ 
                        bgcolor: `${getNoteTypeColor(note.type)}.main`,
                        width: 40, 
                        height: 40 
                      }}>
                        {getNoteIcon(note.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {note.author}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={note.type}
                              size="small"
                              color={getNoteTypeColor(note.type) as any}
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(note.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {note.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < selectedNotes.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotes}>Close</Button>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddNote}>
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={addNoteDialogOpen} onClose={handleCloseAddNote} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Follow-up Note
          {selectedItemForNote && (
            <Typography variant="body2" color="text.secondary">
              For: {selectedItemForNote.name} ({selectedItemForNote.userCode})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Note Type</InputLabel>
              <Select
                value={noteType}
                onChange={(e) => setNoteType(e.target.value as 'call' | 'email' | 'meeting' | 'system')}
                label="Note Type"
              >
                <MenuItem value="call">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" />
                    Call
                  </Box>
                </MenuItem>
                <MenuItem value="email">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email fontSize="small" />
                    Email
                  </Box>
                </MenuItem>
                <MenuItem value="meeting">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Groups fontSize="small" />
                    Meeting
                  </Box>
                </MenuItem>
                <MenuItem value="system">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment fontSize="small" />
                    System Note
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Note Content"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your follow-up note here..."
              margin="normal"
              required
            />
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note will be timestamped and attributed to you automatically.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddNote}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveNote}
            disabled={!newNote.trim()}
          >
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Follow-Up Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Create New Follow-Up</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select
                value={newFollowUp.type}
                onChange={(e) => setNewFollowUp({ ...newFollowUp, type: e.target.value as 'driver' | 'host' })}
                label="Type"
              >
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="host">Host</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title *"
              value={newFollowUp.title}
              onChange={(e) => setNewFollowUp({ ...newFollowUp, title: e.target.value })}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={newFollowUp.description}
              onChange={(e) => setNewFollowUp({ ...newFollowUp, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Urgency</InputLabel>
              <Select
                value={newFollowUp.urgency}
                onChange={(e) => setNewFollowUp({ ...newFollowUp, urgency: e.target.value as 'low' | 'medium' | 'high' })}
                label="Urgency"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Assign To (Optional)</InputLabel>
              <Select
                value={newFollowUp.assignedTo}
                onChange={(e) => setNewFollowUp({ ...newFollowUp, assignedTo: e.target.value })}
                label="Assign To (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="City Manager">City Manager</MenuItem>
                <MenuItem value="Agent">Agent</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="User Email *"
              value={newFollowUp.userEmail}
              onChange={(e) => setNewFollowUp({ ...newFollowUp, userEmail: e.target.value })}
              margin="normal"
              type="email"
              required
            />

            <TextField
              fullWidth
              label="User Phone"
              value={newFollowUp.userPhone}
              onChange={(e) => setNewFollowUp({ ...newFollowUp, userPhone: e.target.value })}
              margin="normal"
            />

            <TextField
              fullWidth
              label="User Name"
              value={newFollowUp.userName}
              onChange={(e) => setNewFollowUp({ ...newFollowUp, userName: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateFollowUp}
            disabled={!newFollowUp.title.trim() || !newFollowUp.userEmail.trim() || isLoading}
          >
            Create Follow-Up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Follow-Up Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Resolve Follow-Up</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You are about to resolve the follow-up for <strong>{selectedItem.userInfo.name}</strong>
                </Typography>
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Follow-Up Details
              </Typography>
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="body2"><strong>Type:</strong> {selectedItem.type.replace('_', ' ')}</Typography>
                <Typography variant="body2"><strong>Title:</strong> {selectedItem.title}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {selectedItem.description}</Typography>
              </Paper>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resolution Notes (optional)"
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="Add any notes about how this issue was resolved..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolveFollowUp}
            disabled={isLoading}
            startIcon={<CheckCircle />}
          >
            Mark as Resolved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default FollowUpDashboardPage;